import React from "react";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

// #124: the assistant sends pricing summaries as lines separated by single "\n".
// CommonMark collapses a lone newline into a space, so without remark-breaks the
// whole summary renders as one run-on paragraph. remark-gfm also enables the
// tables/strikethrough/autolinks the components below are already styled for.
// Shared so every ReactMarkdown in the chat renders newlines identically.
export const chatRemarkPlugins = [remarkGfm, remarkBreaks];

// Shared wrap styles so long tokens (e.g. payment reference IDs) stay inside bubbles.
export const chatTextWrapClassName = "break-words min-w-0 max-w-full";
export const chatTextWrapStyle = { overflowWrap: "anywhere", wordBreak: "break-word" };

export const chatMarkdownComponents = {
  p: ({ node, ...props }) => (
    <p
      className={`mb-3 last:mb-0 leading-7 text-[#1D2739] ${chatTextWrapClassName}`}
      style={chatTextWrapStyle}
      {...props}
    />
  ),
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-[#101928]" {...props} />
  ),
  em: ({ node, ...props }) => (
    <em className="italic text-[#475467]" {...props} />
  ),
  h1: ({ node, ...props }) => (
    <h1 className="text-[18px] font-semibold text-[#101928] mt-5 mb-3 first:mt-0" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-[17px] font-semibold text-[#101928] mt-5 mb-2.5 first:mt-0" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-[16px] font-semibold text-[#101928] mt-4 mb-2 first:mt-0" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 className="text-[15px] font-semibold text-[#101928] mt-4 mb-2 first:mt-0" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc pl-5 mb-3 space-y-1.5 marker:text-[#98A2B3]" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 mb-3 space-y-1.5 marker:text-[#98A2B3]" {...props} />
  ),
  li: ({ node, ...props }) => (
    <li
      className={`leading-7 text-[#1D2739] [&>p]:mb-0 ${chatTextWrapClassName}`}
      style={chatTextWrapStyle}
      {...props}
    />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-[#2746EB] underline underline-offset-2 hover:text-[#1C3BC7] break-words min-w-0 max-w-full"
      style={chatTextWrapStyle}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="border-l-4 border-[#E4E7EC] pl-4 py-1 my-3 text-[#475467] italic"
      {...props}
    />
  ),
  code: ({ node, inline, className, children, ...props }) =>
    inline ? (
      <code
        className={`bg-[#F2F4F7] text-[#101928] px-1.5 py-0.5 rounded text-[0.9em] font-mono break-all max-w-full ${chatTextWrapClassName}`}
        style={{ overflowWrap: "anywhere", wordBreak: "break-all" }}
        {...props}
      >
        {children}
      </code>
    ) : (
      <code
        className="block bg-[#0B0E14] text-[#F2F4F7] p-3 rounded-lg text-[13px] font-mono overflow-x-auto"
        {...props}
      >
        {children}
      </code>
    ),
  pre: ({ node, ...props }) => (
    <pre className="my-3 rounded-lg overflow-x-auto" {...props} />
  ),
  hr: () => <hr className="my-4 border-t border-[#E4E7EC]" />,
  table: ({ node, ...props }) => (
    <div className="my-3 overflow-x-auto">
      <table className="min-w-full text-left text-[14px] border border-[#E4E7EC] rounded-lg" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead className="bg-[#F9FAFB] text-[#475467]" {...props} />
  ),
  th: ({ node, ...props }) => (
    <th className="px-3 py-2 font-semibold border-b border-[#E4E7EC]" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="px-3 py-2 border-b border-[#E4E7EC] align-top" {...props} />
  ),
};

export const formatTimestamp = (timestamp) => {
  const now = new Date();
  const messageTime = new Date(timestamp);
  const diffInSeconds = (now - messageTime) / 1000;
  const diffInMinutes = diffInSeconds / 60;
  const diffInHours = diffInMinutes / 60;
  const diffInDays = diffInHours / 24;

  if (diffInMinutes < 1) {
    return "now";
  } else if (diffInMinutes < 60) {
    return `${Math.floor(diffInMinutes)}m`;
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h`;
  } else if (diffInDays < 7) {
    return `${Math.floor(diffInDays)}d`;
  } else {
    const hours = messageTime.getHours();
    const minutes = messageTime.getMinutes();
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }
};

export const parseChatData = (title) => {
  const result = {
    primaryTitle: "New Chat",
    vin: null,
    route: null,
    raw: title,
    status: null,
    price: 0,
  };

  if (!title) return result;

  try {
    let parsed;
    if (typeof title === 'object') {
      parsed = title;
    } else {
      const decoded = title.replace(/&quot;/g, '"').replace(/&#39;/g, "'");
      parsed = JSON.parse(decoded);
    }
    const { vehicle, shipping, status } = parsed;
    result.status = status;

    if (vehicle) {
      if (vehicle.year && vehicle.make && vehicle.model) {
        const extras = vehicle.trim || vehicle.variant || vehicle.body || "";
        result.primaryTitle = `${vehicle.year} ${vehicle.make} ${
          vehicle.model
        }${extras ? ` ${extras}` : ""}`;
      }
      if (vehicle.vin) {
        result.vin = vehicle.vin;
      }
    }

    if (shipping && shipping.origin && shipping.destination) {
      result.route = `${shipping.origin} → ${shipping.destination}`;
      result.price = shipping.price || 0;
    }
  } catch (e) {
    result.primaryTitle = title !== "null" ? title : "New Chat";
  }
  return result;
};